import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Settings,
  Mail,
  Phone,
  Loader2,
  UserPlus,
  Globe,
  Home,
  Building2,
  Calendar,
  Type,
  LogIn,
  Headset,
  X,
  Check,
  ChevronDown,
} from 'lucide-react';
import { createPortal } from 'react-dom';
import { motion } from 'motion/react';
import { http } from '@/network/http';
import toast from 'react-hot-toast';
import { GlassCard } from '@/components/glass';
import {
  staggerContainer,
  staggerItem,
} from '../utils/adminAnimations';
import { useActiveRoles } from '../hooks/useRoles';

interface SystemSettings {
  companyName_en: string;
  companyName_he: string;
  appName_en: string;
  appName_he: string;
  loginSubtitle_en: string;
  loginSubtitle_he: string;
  contactEmail: string;
  contactPhone: string;
  maintenanceMode: string;
  maintenanceMessage: string;
  primaryColor: string;
  defaultRegistrationRole: string;
  defaultLanguage: string;
  appLaunchMonth: string;
  supportUserIds: string;
}

interface SimpleUser {
  id: string;
  firstName: string | null;
  lastName: string | null;
  avatarUrl: string | null;
}

interface SettingsFormData {
  companyName_en: string;
  companyName_he: string;
  appName_en: string;
  appName_he: string;
  loginSubtitle_en: string;
  loginSubtitle_he: string;
  contactEmail: string;
  contactPhone: string;
  maintenanceMode: boolean;
  maintenanceMessage: string;
  defaultRegistrationRole: string;
  defaultLanguage: string;
  appLaunchMonth: string;
}

/* ── Shared input class ── */
const INPUT_CLS = 'w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium shadow-sm transition-all focus:border-primary-300 focus:ring-2 focus:ring-primary-100 focus:outline-none';

/* ── Bilingual field pair ── */
function BilingualField({ label, icon, registerEn, registerHe }: {
  label: string;
  icon: React.ReactNode;
  registerEn: ReturnType<ReturnType<typeof useForm>['register']>;
  registerHe: ReturnType<ReturnType<typeof useForm>['register']>;
}) {
  return (
    <div className="rounded-xl border border-gray-100 bg-gray-50/50 p-4">
      <div className="mb-3 flex items-center gap-2">
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-white border border-gray-200 shadow-sm text-neutral-500">
          {icon}
        </span>
        <span className="text-sm font-bold text-neutral-700">{label}</span>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 flex items-center gap-1.5 text-xs font-medium text-neutral-400">
            <span className="rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-bold text-blue-600">EN</span>
            English
          </label>
          <input {...registerEn} className={INPUT_CLS} />
        </div>
        <div>
          <label className="mb-1 flex items-center gap-1.5 text-xs font-medium text-neutral-400">
            <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-bold text-emerald-600">HE</span>
            עברית
          </label>
          <input {...registerHe} dir="rtl" className={INPUT_CLS} />
        </div>
      </div>
    </div>
  );
}

export function SystemSettingsPage() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  const { data: roles } = useActiveRoles();

  // Support contacts state
  const [selectedSupportUserIds, setSelectedSupportUserIds] = useState<string[]>([]);
  const [supportUserSearch, setSupportUserSearch] = useState('');
  const [supportDropdownOpen, setSupportDropdownOpen] = useState(false);
  const supportDropdownRef = useRef<HTMLDivElement>(null);

  const { data: allUsers } = useQuery<SimpleUser[]>({
    queryKey: ['settings-users-picker'],
    queryFn: async () => {
      const { data } = await http.get<{ success: boolean; users: SimpleUser[] }>('/settings/users-picker');
      return data.users;
    },
  });

  const LANDING_PAGE_OPTIONS = [
    { value: '', label: t('settings.landingPageNotSet') },
    { value: '/home', label: t('nav.home') },
    { value: '/reports', label: t('nav.reports') },
    { value: '/feed', label: t('nav.feed') },
    { value: '/dashboard', label: t('nav.dashboard') },
    { value: '/messages', label: t('nav.chat') },
    { value: '/admin', label: `${t('nav.admin')} - ${t('adminNav.dashboard')}` },
    { value: '/admin/payroll', label: `${t('nav.admin')} - ${t('adminNav.payroll')}` },
    { value: '/admin/approvals', label: `${t('nav.admin')} - ${t('adminNav.reportApprovals')}` },
  ];

  const updateRoleLandingPage = useMutation({
    mutationFn: async ({ id, defaultLandingPage }: { id: string; defaultLandingPage: string | null }) => {
      const { data: res } = await http.put(`/roles/${id}`, { defaultLandingPage });
      return res.role;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      toast.success(t('settings.landingPageSaved'));
    },
    onError: () => {
      toast.error(t('common.error'));
    },
  });

  const handleLandingPageChange = useCallback((roleId: string, value: string) => {
    updateRoleLandingPage.mutate({ id: roleId, defaultLandingPage: value || null });
  }, [updateRoleLandingPage]);

  const { data: settings, isLoading } = useQuery<SystemSettings>({
    queryKey: ['system-settings'],
    queryFn: async () => {
      const { data } = await http.get('/settings');
      return data.settings;
    },
  });

  const updateSettings = useMutation({
    mutationFn: async (data: Partial<SystemSettings>) => {
      const { data: res } = await http.put('/settings', data);
      return res.settings;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-settings'] });
      toast.success(t('settings.saved'));
    },
    onError: () => {
      toast.error(t('common.error'));
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<SettingsFormData>();

  useEffect(() => {
    if (settings) {
      reset({
        companyName_en: settings.companyName_en,
        companyName_he: settings.companyName_he,
        appName_en: settings.appName_en,
        appName_he: settings.appName_he,
        loginSubtitle_en: settings.loginSubtitle_en,
        loginSubtitle_he: settings.loginSubtitle_he,
        contactEmail: settings.contactEmail,
        contactPhone: settings.contactPhone,
        maintenanceMode: settings.maintenanceMode === 'true',
        maintenanceMessage: settings.maintenanceMessage,
        defaultRegistrationRole: settings.defaultRegistrationRole || 'EMP',
        defaultLanguage: settings.defaultLanguage || 'en',
        appLaunchMonth: settings.appLaunchMonth || '2026-03',
      });
      // Initialize support user IDs from settings
      const ids = (settings.supportUserIds || '').split(',').map(s => s.trim()).filter(Boolean);
      setSelectedSupportUserIds(ids);
    }
  }, [settings, reset]);

  const onSubmit = handleSubmit((data) => {
    updateSettings.mutate({
      companyName_en: data.companyName_en,
      companyName_he: data.companyName_he,
      appName_en: data.appName_en,
      appName_he: data.appName_he,
      loginSubtitle_en: data.loginSubtitle_en,
      loginSubtitle_he: data.loginSubtitle_he,
      contactEmail: data.contactEmail,
      contactPhone: data.contactPhone,
      maintenanceMode: String(data.maintenanceMode),
      maintenanceMessage: data.maintenanceMessage,
      defaultRegistrationRole: data.defaultRegistrationRole,
      defaultLanguage: data.defaultLanguage,
      appLaunchMonth: data.appLaunchMonth,
      supportUserIds: selectedSupportUserIds.join(','),
    });
  });

  const filteredUsers = allUsers?.filter((u) => {
    if (!supportUserSearch) return true;
    const name = `${u.firstName || ''} ${u.lastName || ''}`.toLowerCase();
    return name.includes(supportUserSearch.toLowerCase());
  }) ?? [];

  const selectedUsers = allUsers?.filter((u) => selectedSupportUserIds.includes(u.id)) ?? [];

  const initialSupportIds = (settings?.supportUserIds || '').split(',').map(s => s.trim()).filter(Boolean);
  const supportUsersDirty = selectedSupportUserIds.join(',') !== initialSupportIds.join(',');

  if (isLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ type: 'spring' as const, stiffness: 350, damping: 30 }}
          className="rounded-xl border border-gray-200 p-8 shadow-sm glass-surface"
        >
          <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
        </motion.div>
      </div>
    );
  }

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
      className="space-y-6 pb-8"
    >
      {/* ── Page Header ── */}
      <motion.div variants={staggerItem} className="space-y-4">
        <div className="inline-flex items-center justify-center rounded-xl border border-gray-200 p-3 shadow-sm glass-surface">
          <Settings className="h-6 w-6 text-slate-800" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-neutral-900">{t('settings.systemSettings')}</h1>
          <p className="mt-1 text-sm text-neutral-600">{t('settings.systemDescription')}</p>
        </div>
      </motion.div>

      <form onSubmit={onSubmit} className="space-y-6">

        {/* ════════════════════════════════════════════
            1. GENERAL DEFAULTS
            ════════════════════════════════════════════ */}
        <motion.div variants={staggerItem}>
          <GlassCard title={t('settings.general')} icon={<Settings className="h-4 w-4" />}>
            <div className="grid gap-4 sm:grid-cols-3">
              {/* Registration Role */}
              <div>
                <label className="mb-1.5 flex items-center gap-2 text-sm font-semibold text-neutral-700">
                  <UserPlus className="h-3.5 w-3.5 text-neutral-400" />
                  {t('settings.defaultRegistrationRole')}
                </label>
                <select {...register('defaultRegistrationRole')} className={INPUT_CLS}>
                  {roles?.map((role) => (
                    <option key={role.id} value={role.code}>
                      {i18n.language === 'he' ? role.name : (role.nameEn || role.name)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Language */}
              <div>
                <label className="mb-1.5 flex items-center gap-2 text-sm font-semibold text-neutral-700">
                  <Globe className="h-3.5 w-3.5 text-neutral-400" />
                  {t('settings.defaultLanguage')}
                </label>
                <select {...register('defaultLanguage')} className={INPUT_CLS}>
                  <option value="en">English</option>
                  <option value="he">עברית</option>
                </select>
              </div>

              {/* Launch Month */}
              <div>
                <label className="mb-1.5 flex items-center gap-2 text-sm font-semibold text-neutral-700">
                  <Calendar className="h-3.5 w-3.5 text-neutral-400" />
                  {t('settings.appLaunchMonth')}
                </label>
                <input
                  {...register('appLaunchMonth')}
                  type="month"
                  className={INPUT_CLS}
                />
              </div>
            </div>
          </GlassCard>
        </motion.div>

        {/* ════════════════════════════════════════════
            2. BRANDING
            ════════════════════════════════════════════ */}
        <motion.div variants={staggerItem}>
          <GlassCard title={t('settings.branding')} icon={<Building2 className="h-4 w-4" />}>
            <div className="space-y-3">
              <BilingualField
                label={t('settings.companyName')}
                icon={<Building2 className="h-3.5 w-3.5" />}
                registerEn={register('companyName_en')}
                registerHe={register('companyName_he')}
              />
              <BilingualField
                label={t('settings.appName')}
                icon={<Type className="h-3.5 w-3.5" />}
                registerEn={register('appName_en')}
                registerHe={register('appName_he')}
              />
              <BilingualField
                label={t('settings.loginSubtitle')}
                icon={<LogIn className="h-3.5 w-3.5" />}
                registerEn={register('loginSubtitle_en')}
                registerHe={register('loginSubtitle_he')}
              />
            </div>
          </GlassCard>
        </motion.div>

        {/* ════════════════════════════════════════════
            3. CONTACT & SUPPORT
            ════════════════════════════════════════════ */}
        <motion.div variants={staggerItem}>
          <GlassCard title={t('settings.contactInfo')} icon={<Mail className="h-4 w-4" />}>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 flex items-center gap-2 text-sm font-semibold text-neutral-700">
                  <Mail className="h-3.5 w-3.5 text-neutral-400" />
                  {t('settings.contactEmail')}
                </label>
                <input
                  {...register('contactEmail')}
                  type="email"
                  placeholder="support@company.com"
                  className={INPUT_CLS}
                />
              </div>
              <div>
                <label className="mb-1.5 flex items-center gap-2 text-sm font-semibold text-neutral-700">
                  <Phone className="h-3.5 w-3.5 text-neutral-400" />
                  {t('settings.contactPhone')}
                </label>
                <input
                  {...register('contactPhone')}
                  type="tel"
                  placeholder="+972-XX-XXX-XXXX"
                  className={INPUT_CLS}
                />
              </div>
            </div>
          </GlassCard>
        </motion.div>

        {/* ════════════════════════════════════════════
            3.5. SUPPORT CONTACTS
            ════════════════════════════════════════════ */}
        <motion.div variants={staggerItem}>
          <GlassCard title={t('settings.supportContacts')} icon={<Headset className="h-4 w-4" />}>
            <p className="mb-3 text-sm text-neutral-500">{t('settings.supportContactsDesc')}</p>

            <div className="relative" ref={supportDropdownRef}>
              {/* Trigger button styled like a select */}
              <button
                type="button"
                onClick={() => setSupportDropdownOpen((v) => !v)}
                className={`${INPUT_CLS} flex items-center gap-2 text-start`}
              >
                {selectedUsers.length > 0 ? (
                  <div className="flex flex-1 flex-wrap gap-1.5 min-w-0">
                    {selectedUsers.map((u) => (
                      <span
                        key={u.id}
                        className="inline-flex items-center gap-1 rounded-md bg-primary-50 border border-primary-200 px-2 py-0.5 text-xs font-semibold text-primary-700"
                      >
                        {u.firstName} {u.lastName}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedSupportUserIds((prev) => prev.filter((id) => id !== u.id));
                          }}
                          className="ml-0.5 rounded hover:bg-primary-100"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className="flex-1 text-neutral-400">{t('common.select')}</span>
                )}
                <ChevronDown className={`h-4 w-4 shrink-0 text-neutral-400 transition-transform ${supportDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Dropdown — portaled to body to escape overflow:hidden */}
              {supportDropdownOpen && createPortal(
                <>
                  <div className="fixed inset-0 z-[9998]" onClick={() => { setSupportDropdownOpen(false); setSupportUserSearch(''); }} />
                  <div
                    className="fixed z-[9999] rounded-xl border border-gray-200 bg-white shadow-lg"
                    style={(() => {
                      const rect = supportDropdownRef.current?.getBoundingClientRect();
                      if (!rect) return {};
                      return { top: rect.bottom + 4, left: rect.left, width: rect.width };
                    })()}
                  >
                    {/* Search */}
                    <div className="border-b border-gray-100 p-2">
                      <input
                        type="text"
                        value={supportUserSearch}
                        onChange={(e) => setSupportUserSearch(e.target.value)}
                        placeholder={t('common.search')}
                        className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:border-primary-300 focus:ring-1 focus:ring-primary-100 focus:outline-none"
                        autoFocus
                      />
                    </div>
                    {/* User list with checkboxes */}
                    <div className="max-h-56 overflow-y-auto py-1">
                      {filteredUsers.length === 0 ? (
                        <p className="px-3 py-4 text-center text-sm text-neutral-400">{t('common.noResults')}</p>
                      ) : (
                        filteredUsers.map((u) => {
                          const isSelected = selectedSupportUserIds.includes(u.id);
                          return (
                            <button
                              key={u.id}
                              type="button"
                              onClick={() => {
                                setSelectedSupportUserIds((prev) =>
                                  isSelected ? prev.filter((id) => id !== u.id) : [...prev, u.id]
                                );
                              }}
                              className={`flex w-full items-center gap-2.5 px-3 py-2 text-sm transition-colors hover:bg-gray-50 ${isSelected ? 'bg-primary-50/50' : ''}`}
                            >
                              <div className={`flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded border ${isSelected ? 'border-primary-500 bg-primary-500' : 'border-gray-300 bg-white'}`}>
                                {isSelected && <Check className="h-3 w-3 text-white" />}
                              </div>
                              {u.avatarUrl ? (
                                <img src={u.avatarUrl} alt="" className="h-6 w-6 rounded-full object-cover" />
                              ) : (
                                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-500/15 text-[10px] font-bold text-primary-700">
                                  {u.firstName?.[0]}{u.lastName?.[0]}
                                </span>
                              )}
                              <span className="flex-1 text-start text-neutral-700">{u.firstName} {u.lastName}</span>
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>
                </>,
                document.body
              )}
            </div>
          </GlassCard>
        </motion.div>

        {/* ════════════════════════════════════════════
            4. LANDING PAGES PER ROLE
            ════════════════════════════════════════════ */}
        <motion.div variants={staggerItem}>
          <GlassCard title={t('settings.defaultLandingPages')} icon={<Home className="h-4 w-4" />}>
            <p className="mb-4 text-sm text-neutral-500">{t('settings.defaultLandingPagesDesc')}</p>
            <div className="space-y-2.5">
              {roles?.map((role) => (
                <div key={role.id} className="flex items-center gap-3 rounded-xl border border-gray-100 bg-gray-50/50 px-4 py-2.5">
                  <span
                    className="inline-flex min-w-[110px] items-center justify-center rounded-lg px-2.5 py-1 text-xs font-bold text-white shadow-sm"
                    style={{ backgroundColor: role.color || '#6b7280' }}
                  >
                    {i18n.language === 'he' ? role.name : (role.nameEn || role.name)}
                  </span>
                  <select
                    defaultValue={role.defaultLandingPage || ''}
                    onChange={(e) => handleLandingPageChange(role.id, e.target.value)}
                    className={`flex-1 ${INPUT_CLS}`}
                  >
                    {LANDING_PAGE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}{opt.value ? ` (${opt.value})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </GlassCard>
        </motion.div>

        {/* ── Save ── */}
        <motion.div variants={staggerItem} className="flex justify-end pt-2">
          <button
            type="submit"
            disabled={(!isDirty && !supportUsersDirty) || updateSettings.isPending}
            className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-primary-600 px-6 py-3 text-sm font-bold text-white shadow-sm transition-all hover:bg-primary-700 active:translate-y-px active:shadow-sm disabled:cursor-not-allowed disabled:opacity-50 disabled:active:translate-y-0 disabled:active:shadow-sm"
          >
            {updateSettings.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('common.save')}
          </button>
        </motion.div>
      </form>
    </motion.div>
  );
}
